import React, { ReactNode } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../context/ThemeContext';
import { createThemedStyles } from '../../theme/useThemedStyles';
import { isWeb } from '../../utils/platformUtils';

export type WebPageHeaderProps = {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  headerAction?: ReactNode;
};

/** Desktop web page title card — same width as WebPage content column. */
const WebPageHeader = ({ title, subtitle, onBack, headerAction }: WebPageHeaderProps) => {
  const { colors } = useTheme();
  const styles = useWebPageHeaderStyles();

  if (!isWeb) {
    return null;
  }

  return (
    <View
      style={[
        styles.card,
        {
          backgroundColor: colors.grey[100],
          borderColor: colors.grey[200],
        },
      ]}
    >
      <View style={styles.cardRow}>
        {onBack ? (
          <TouchableOpacity
            onPress={onBack}
            style={styles.backButton}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="arrow-back" size={22} color={colors.text.primary} />
          </TouchableOpacity>
        ) : null}
        <View style={styles.titleBlock}>
          <Text style={[styles.title, { color: colors.text.primary }]}>{title}</Text>
          {subtitle ? (
            <Text style={[styles.subtitle, { color: colors.text.secondary }]}>{subtitle}</Text>
          ) : null}
        </View>
        {headerAction ? <View style={styles.headerAction}>{headerAction}</View> : null}
      </View>
    </View>
  );
};

const useWebPageHeaderStyles = createThemedStyles((colors) => ({
  card: {
    width: '100%',
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  backButton: {
    marginRight: 12,
    marginTop: 2,
    padding: 4,
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
  },
  headerAction: {
    marginLeft: 16,
    flexShrink: 0,
    alignSelf: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: 14,
    marginTop: 4,
    lineHeight: 20,
  },
}));

export default WebPageHeader;
